import { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { adminAPI } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  TrendingUp, TrendingDown, Zap, RefreshCw, Search, X
} from 'lucide-react';
import { toast } from 'sonner';

const fmt = (v) => v == null ? '$0.00' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(v);
const fmtP = (v) => v == null ? '$0.00' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 6 }).format(v);

export default function AdminSimulationPage() {
  const [openTrades, setOpenTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tradeSearch, setTradeSearch] = useState('');

  // Trade override
  const [overrideTradeId, setOverrideTradeId] = useState(null);
  const [overrideForm, setOverrideForm] = useState({ custom_price: '', target_pnl: '' });
  const [overrideMode, setOverrideMode] = useState('pnl');

  // Trade modify
  const [modifyTradeId, setModifyTradeId] = useState(null);
  const [modifyForm, setModifyForm] = useState({ entry_price: '', stop_loss: '', take_profit: '', profit_multiplier: '' });

  // Force close
  const [closingTradeId, setClosingTradeId] = useState(null);
  const [closePrice, setClosePrice] = useState('');

  const loadData = useCallback(async () => {
    try {
      const res = await adminAPI.getOpenTradesAdmin();
      setOpenTrades(res.data);
    } catch { toast.error('Failed to load trades'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Auto-refresh every 5s
  useEffect(() => {
    const iv = setInterval(() => {
      adminAPI.getOpenTradesAdmin().then(r => setOpenTrades(r.data)).catch(() => {});
    }, 5000);
    return () => clearInterval(iv);
  }, []);

  const handleTradeOverride = async (tradeId) => {
    const payload = {};
    if (overrideMode === 'price' && overrideForm.custom_price) {
      payload.custom_price = parseFloat(overrideForm.custom_price);
    } else if (overrideMode === 'pnl' && overrideForm.target_pnl !== '') {
      payload.target_pnl = parseFloat(overrideForm.target_pnl);
    } else { toast.error('Enter a value'); return; }
    try {
      const { data } = await adminAPI.setTradeOverride(tradeId, payload);
      toast.success(`P/L set to ${fmt(data.override.resulting_pnl)}`);
      setOverrideTradeId(null);
      setOverrideForm({ custom_price: '', target_pnl: '' });
      await loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Override failed'); }
  };

  const handleForceClose = async (tradeId) => {
    const price = closePrice ? parseFloat(closePrice) : null;
    try {
      const { data } = await adminAPI.forceCloseTrade(tradeId, price);
      toast.success(`Trade closed — P/L: ${fmt(data.profit_loss)}`);
      setClosingTradeId(null);
      setClosePrice('');
      await loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Close failed'); }
  };

  const handleModifyTrade = async (tradeId) => {
    const payload = { trade_id: tradeId };
    if (modifyForm.entry_price) payload.entry_price = parseFloat(modifyForm.entry_price);
    if (modifyForm.stop_loss) payload.stop_loss = parseFloat(modifyForm.stop_loss);
    if (modifyForm.take_profit) payload.take_profit = parseFloat(modifyForm.take_profit);
    if (modifyForm.profit_multiplier) payload.profit_multiplier = parseFloat(modifyForm.profit_multiplier);
    try {
      await adminAPI.modifyTrade(payload);
      toast.success('Trade modified');
      setModifyTradeId(null);
      setModifyForm({ entry_price: '', stop_loss: '', take_profit: '', profit_multiplier: '' });
      await loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Modify failed'); }
  };

  const filtered = openTrades.filter(t => !tradeSearch ||
    t.user_name?.toLowerCase().includes(tradeSearch.toLowerCase()) ||
    t.symbol?.toLowerCase().includes(tradeSearch.toLowerCase()) ||
    t.user_id?.includes(tradeSearch)
  );

  if (loading) return (
    <AdminLayout><div className="flex items-center justify-center h-60"><div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin" /></div></AdminLayout>
  );

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-trade-control-page">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold font-['Outfit'] text-[var(--text-primary)]">Trade Controls</h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1">Force profit/loss, close trades, modify entries — {openTrades.length} open</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative w-full sm:w-72">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]" />
              <Input value={tradeSearch} onChange={e => setTradeSearch(e.target.value)} placeholder="Search by user, symbol, ID..."
                data-testid="trade-control-search"
                className="pl-9 h-10 bg-[var(--bg-surface)] border-[var(--border-subtle)] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]" />
            </div>
            <button onClick={loadData} data-testid="trade-control-refresh" className="p-2.5 rounded-lg hover:bg-[var(--hover-bg)] text-[var(--text-secondary)]"><RefreshCw className="w-4 h-4" /></button>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="p-12 text-center text-sm text-[var(--text-tertiary)] bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg">
            {openTrades.length === 0 ? 'No open trades' : 'No trades match your search'}
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(trade => (
              <div key={trade.id} className={`p-4 rounded-lg border transition-all bg-[var(--bg-surface)] ${trade.has_override ? 'border-amber-500/30' : 'border-[var(--border-subtle)]'}`} data-testid={`tc-trade-${trade.id}`}>
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${trade.type === 'buy' ? 'bg-green-500/15' : 'bg-red-500/15'}`}>
                      {trade.type === 'buy' ? <TrendingUp className="w-4 h-4 text-green-400" /> : <TrendingDown className="w-4 h-4 text-red-400" />}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs font-bold ${trade.type === 'buy' ? 'text-green-400' : 'text-red-400'}`}>{trade.type.toUpperCase()}</span>
                        <span className="text-xs font-semibold text-[var(--text-primary)] uppercase">{trade.symbol}</span>
                        <span className="text-[10px] text-[var(--text-tertiary)]">vol: {trade.volume}</span>
                        {trade.has_override && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/15 text-amber-400">OVERRIDE</span>}
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-[var(--text-secondary)] mt-0.5">
                        <span className="font-medium">{trade.user_name}</span>
                        <span className="text-[var(--text-tertiary)]">#{trade.user_id}</span>
                        <span className="text-[var(--text-tertiary)]">|</span>
                        <span>Entry: <span className="font-mono">{fmtP(trade.entry_price)}</span></span>
                        <span data-testid={`tc-real-price-${trade.id}`}>Market: <span className="font-mono">{fmtP(trade.real_current_price ?? trade.current_price)}</span></span>
                        {trade.has_override && (
                          <span className="text-amber-400" data-testid={`tc-user-price-${trade.id}`}>User sees: <span className="font-mono">{fmtP(trade.synthetic_user_price ?? trade.current_price)}</span></span>
                        )}
                        {trade.spread_amount > 0 && <><span className="text-[var(--text-tertiary)]">|</span><span className="text-amber-400">Spread: {fmt(trade.spread_amount)}</span></>}
                        <span className="text-[var(--text-tertiary)]">|</span>
                        <span>{trade.opened_at ? new Date(trade.opened_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-right min-w-[110px]">
                      <p className={`text-sm font-bold font-mono ${trade.profit_loss >= 0 ? 'text-green-400' : 'text-red-400'}`} data-testid={`tc-pnl-${trade.id}`}>
                        {trade.profit_loss >= 0 ? '+' : ''}{fmt(trade.profit_loss)}
                      </p>
                      <p className="text-[10px] text-[var(--text-tertiary)] font-mono" data-testid={`tc-real-pnl-${trade.id}`}>
                        real {trade.real_profit_loss >= 0 ? '+' : ''}{fmt(trade.real_profit_loss ?? trade.profit_loss)}
                      </p>
                      {!!trade.pnl_offset && (
                        <p className="text-[10px] text-amber-400 font-mono" data-testid={`tc-offset-${trade.id}`}>
                          offset {trade.pnl_offset >= 0 ? '+' : ''}{fmt(trade.pnl_offset)}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-1.5">
                      <button onClick={() => { setOverrideTradeId(overrideTradeId === trade.id ? null : trade.id); setOverrideMode('pnl'); setOverrideForm({ custom_price: String(trade.current_price), target_pnl: '' }); setModifyTradeId(null); setClosingTradeId(null); }}
                        data-testid={`tc-set-pnl-${trade.id}`}
                        className="px-2.5 py-1.5 rounded text-[10px] font-medium bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border border-amber-500/20">Set P/L</button>
                      <button onClick={() => { setModifyTradeId(modifyTradeId === trade.id ? null : trade.id); setOverrideTradeId(null); setClosingTradeId(null); setModifyForm({ entry_price: '', stop_loss: '', take_profit: '', profit_multiplier: '' }); }}
                        data-testid={`tc-modify-${trade.id}`}
                        className="px-2.5 py-1.5 rounded text-[10px] font-medium bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 border border-blue-500/20">Modify</button>
                      <button onClick={() => { setClosingTradeId(closingTradeId === trade.id ? null : trade.id); setOverrideTradeId(null); setModifyTradeId(null); setClosePrice(''); }}
                        data-testid={`tc-close-${trade.id}`}
                        className="px-2.5 py-1.5 rounded text-[10px] font-medium bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20">Close</button>
                    </div>
                  </div>
                </div>

                {/* Set P/L Panel */}
                {overrideTradeId === trade.id && (
                  <div className="mt-3 pt-3 border-t border-[var(--border-subtle)] space-y-3" data-testid={`tc-override-panel-${trade.id}`}>
                    <div className="flex gap-2">
                      <button onClick={() => setOverrideMode('pnl')} className={`px-3 py-1.5 rounded text-[10px] font-medium transition-colors ${overrideMode === 'pnl' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'text-[var(--text-secondary)] bg-[var(--bg-primary)]'}`}>Target P/L</button>
                      <button onClick={() => setOverrideMode('price')} className={`px-3 py-1.5 rounded text-[10px] font-medium transition-colors ${overrideMode === 'price' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'text-[var(--text-secondary)] bg-[var(--bg-primary)]'}`}>Set Price</button>
                    </div>
                    <div className="flex gap-2 items-end">
                      {overrideMode === 'pnl' ? (
                        <div className="flex-1">
                          <Label className="text-[10px] text-[var(--text-secondary)]">Target P/L ($) — positive for profit, negative for loss</Label>
                          <Input type="number" step="any" value={overrideForm.target_pnl} onChange={e => setOverrideForm({...overrideForm, target_pnl: e.target.value})} placeholder="e.g. 500 or -200"
                            data-testid={`tc-pnl-input-${trade.id}`}
                            className="h-9 text-xs bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)]" />
                        </div>
                      ) : (
                        <div className="flex-1">
                          <Label className="text-[10px] text-[var(--text-secondary)]">Custom Current Price</Label>
                          <Input type="number" step="any" value={overrideForm.custom_price} onChange={e => setOverrideForm({...overrideForm, custom_price: e.target.value})}
                            data-testid={`tc-price-input-${trade.id}`}
                            className="h-9 text-xs bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)]" />
                        </div>
                      )}
                      <button onClick={() => { setOverrideMode('pnl'); setOverrideForm({...overrideForm, target_pnl: '500'}); }}
                        className="h-9 px-3 rounded text-[10px] font-medium bg-green-500/10 text-green-400 hover:bg-green-500/20 border border-green-500/20 shrink-0">
                        <TrendingUp className="w-3 h-3 inline mr-1" />Profit
                      </button>
                      <button onClick={() => { setOverrideMode('pnl'); setOverrideForm({...overrideForm, target_pnl: '-500'}); }}
                        className="h-9 px-3 rounded text-[10px] font-medium bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 shrink-0">
                        <TrendingDown className="w-3 h-3 inline mr-1" />Loss
                      </button>
                      <button onClick={() => handleTradeOverride(trade.id)} data-testid={`tc-override-apply-${trade.id}`}
                        className="h-9 px-4 rounded text-[10px] font-medium bg-amber-500 text-white hover:bg-amber-600 shrink-0">Apply</button>
                      <button onClick={() => setOverrideTradeId(null)} className="h-9 px-2 text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                )}

                {/* Modify Panel */}
                {modifyTradeId === trade.id && (
                  <div className="mt-3 pt-3 border-t border-[var(--border-subtle)]" data-testid={`tc-modify-panel-${trade.id}`}>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                      <div>
                        <Label className="text-[10px] text-[var(--text-secondary)]">Entry Price</Label>
                        <Input type="number" step="any" value={modifyForm.entry_price} onChange={e => setModifyForm({...modifyForm, entry_price: e.target.value})} placeholder={String(trade.entry_price)}
                          className="h-9 text-xs bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)]" />
                      </div>
                      <div>
                        <Label className="text-[10px] text-[var(--text-secondary)]">Stop Loss</Label>
                        <Input type="number" step="any" value={modifyForm.stop_loss} onChange={e => setModifyForm({...modifyForm, stop_loss: e.target.value})} placeholder={trade.stop_loss ? String(trade.stop_loss) : 'None'}
                          className="h-9 text-xs bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)]" />
                      </div>
                      <div>
                        <Label className="text-[10px] text-[var(--text-secondary)]">Take Profit</Label>
                        <Input type="number" step="any" value={modifyForm.take_profit} onChange={e => setModifyForm({...modifyForm, take_profit: e.target.value})} placeholder={trade.take_profit ? String(trade.take_profit) : 'None'}
                          className="h-9 text-xs bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)]" />
                      </div>
                      <div>
                        <Label className="text-[10px] text-[var(--text-secondary)]">Profit Multiplier</Label>
                        <Input type="number" step="0.1" value={modifyForm.profit_multiplier} onChange={e => setModifyForm({...modifyForm, profit_multiplier: e.target.value})} placeholder="e.g. 2.0"
                          className="h-9 text-xs bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)]" />
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => handleModifyTrade(trade.id)} data-testid={`tc-modify-apply-${trade.id}`}
                        className="px-4 py-2 rounded text-xs font-medium bg-blue-500 text-white hover:bg-blue-600">Apply Changes</button>
                      <button onClick={() => setModifyTradeId(null)} className="px-4 py-2 rounded text-xs font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)]">Cancel</button>
                    </div>
                  </div>
                )}

                {/* Force Close Panel */}
                {closingTradeId === trade.id && (
                  <div className="mt-3 pt-3 border-t border-[var(--border-subtle)] flex items-end gap-3" data-testid={`tc-close-panel-${trade.id}`}>
                    <div className="flex-1">
                      <Label className="text-[10px] text-[var(--text-secondary)]">Close Price (empty = current price, user receives current P/L)</Label>
                      <Input type="number" step="any" value={closePrice} onChange={e => setClosePrice(e.target.value)} placeholder={String(trade.current_price)}
                        data-testid={`tc-close-price-${trade.id}`}
                        className="h-9 text-xs bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)]" />
                    </div>
                    <button onClick={() => handleForceClose(trade.id)} data-testid={`tc-close-confirm-${trade.id}`}
                      className="h-9 px-4 rounded text-xs font-medium bg-red-500 text-white hover:bg-red-600 shrink-0">Force Close</button>
                    <button onClick={() => setClosingTradeId(null)} className="h-9 px-2 text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"><X className="w-3.5 h-3.5" /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
